import { Controller } from "@hotwired/stimulus"

// The dashboard's card switcher (budgets/_card_switcher). Pressing and holding
// the card opens a sideways strip with one ring card per carousel card; a
// one-time note (budgets/_card_switcher_note) teaches the hold. A pick closes
// the strip, then hands the index to budget_carousel_controller#pickCard
// (card-switcher:pick), so the carousel glides the way a swipe does.
//
// Three states, one at a time: idle → pressing (finger down, timing the hold) →
// open (strip up). Every way out of a state lands back in idle.
//
// The finger that held the card is still down when the strip appears under
// it, so its release (the click WebKit synthesises on lift) would
// land on a ring card and pick it. That release is swallowed, document-wide.
// Same for the tap that closes the strip from outside: it closes, it does not
// also press whatever was under it.
//
// Motion (budgets/_card_switcher_styles): a frosted band covers the card as
// the ring cards rise, spring in, quick ease-out out. The card is never touched.
const COACH_KEY = "floosyo:card-switcher-coach-done"
// A deliberate hold, not a lingering tap. iOS's own long-press is switched
// off on the stage (budgets/_card_switcher_styles), so it never races this.
const HOLD_MS = 550
const HOLD_SLOP = 8
const COACH_DELAY_MS = 1000
const PICK_DELAY_MS = 160
// How long the click WebKit synthesises may trail the lift it belongs to.
const RELEASE_MS = 400

export default class extends Controller {
  static targets = ["view", "strip", "item", "coach"]

  connect() {
    this.stage = this.element.querySelector('[data-budget-carousel-target="stage"]')
    this.carousel = this.element.closest('[data-controller~="budget-carousel"]')
    this._state = "idle"
    this._swallowUntil = 0

    this._onDown = this._down.bind(this)
    this._onMove = this._move.bind(this)
    this._onUp = this._up.bind(this)
    this._onDocDown = this._docDown.bind(this)
    this._onDocUp = this._docUp.bind(this)
    this._onDocRelease = this._docRelease.bind(this)
    this._onNative = (event) => event.preventDefault()
    this._onReset = () => this._reset()

    this.element.classList.add("cs-host")
    this.stage?.addEventListener("pointerdown", this._onDown)
    // Capture: runs before the carousel's own swipe listener on the stage.
    this.stage?.addEventListener("pointermove", this._onMove, true)
    this.stage?.addEventListener("pointerup", this._onUp)
    this.stage?.addEventListener("pointercancel", this._onUp)
    // No callout, image lift or drag preview from a held card.
    this.stage?.addEventListener("contextmenu", this._onNative)
    this.stage?.addEventListener("dragstart", this._onNative)
    document.addEventListener("pointerdown", this._onDocDown, true)
    document.addEventListener("pointerup", this._onDocUp, true)
    document.addEventListener("click", this._onDocRelease, true)
    document.addEventListener("turbo:before-cache", this._onReset)
    document.addEventListener("visibilitychange", this._onReset)

    if (this.itemTargets.length > 1 && !this._coachDone()) {
      this._coachTimer = setTimeout(() => this.element.classList.add("cs-coaching"), COACH_DELAY_MS)
    }
  }

  disconnect() {
    this._reset()
    clearTimeout(this._coachTimer)
    this.stage?.removeEventListener("pointerdown", this._onDown)
    this.stage?.removeEventListener("pointermove", this._onMove, true)
    this.stage?.removeEventListener("pointerup", this._onUp)
    this.stage?.removeEventListener("pointercancel", this._onUp)
    this.stage?.removeEventListener("contextmenu", this._onNative)
    this.stage?.removeEventListener("dragstart", this._onNative)
    document.removeEventListener("pointerdown", this._onDocDown, true)
    document.removeEventListener("pointerup", this._onDocUp, true)
    document.removeEventListener("click", this._onDocRelease, true)
    document.removeEventListener("turbo:before-cache", this._onReset)
    document.removeEventListener("visibilitychange", this._onReset)
    this.element.classList.remove("cs-host", "cs-coaching")
  }

  // ── Actions ──────────────────────────────────────────────────────

  open() {
    if (this._state === "open" || this.itemTargets.length < 2) return
    this.dismissCoach()
    // Everything is laid out and scrolled while the band is still hidden,
    // so the only motion anyone sees is the cards rising.
    this._place()
    this._reveal(this.itemTargets[this._activeIndex()])
    this._setState("open")
  }

  close() {
    if (this._state !== "open") return
    this._setState("idle")
  }

  pick(event) {
    if (this._state !== "open" || this._picking) return
    this._picking = true
    const item = event.currentTarget
    const index = parseInt(item.dataset.index, 10)
    item.classList.remove("is-pick")
    void item.offsetWidth
    item.classList.add("is-pick")
    this._pickTimer = setTimeout(() => {
      this._picking = false
      this.close()
      this.dispatch("pick", { detail: { index } })
    }, PICK_DELAY_MS)
  }

  dismissCoach() {
    clearTimeout(this._coachTimer)
    this.element.classList.remove("cs-coaching")
    try { localStorage.setItem(COACH_KEY, "1") } catch { /* storage blocked: the note shows again next visit */ }
  }

  // ── The hold ─────────────────────────────────────────────────────

  _down(event) {
    if (this._state !== "idle" || !event.isPrimary) return
    // The tap that just closed the strip is not a new hold.
    if (Date.now() < this._swallowUntil) return
    if (event.target.closest("button, a, .bank-sync-toggle, .bank-sync-strip")) return
    this._x0 = event.clientX
    this._y0 = event.clientY
    this._setState("pressing")
    this._holdTimer = setTimeout(() => {
      this.open()
      // The finger is still down: its lift belongs to this hold (_docUp).
      this._heldOpen = this._state === "open"
    }, HOLD_MS)
  }

  _move(event) {
    // The finger that opened the strip is still down: it must not swipe the
    // carousel under the strip.
    if (this._state === "open") { event.stopPropagation(); return }
    if (this._state !== "pressing") return
    if (Math.abs(event.clientX - this._x0) > HOLD_SLOP || Math.abs(event.clientY - this._y0) > HOLD_SLOP) this._setState("idle")
  }

  _up() {
    if (this._state === "pressing") this._setState("idle")
  }

  // ── Document ─────────────────────────────────────────────────────

  // A tap outside the open strip closes it, and only closes it.
  _docDown(event) {
    if (this._state !== "open" || this.viewTarget.contains(event.target)) return
    // Blocks the stage's own pointerdown on this same press (_down), and the
    // lift re-arms it for the click (_docUp).
    this._swallowUntil = Date.now() + RELEASE_MS
    this._heldOpen = true
    this.close()
  }

  // The lift of the finger that held the strip open, or that tapped outside
  // to close it: however long it was down, the click that follows is not a
  // pick, and not a tap on what was under the strip.
  _docUp() {
    if (!this._heldOpen) return
    this._heldOpen = false
    this._swallowUntil = Date.now() + RELEASE_MS
  }

  // Swallows the click of the gesture that opened or closed the strip,
  // wherever the finger lifts (pointerup still reaches the carousel, which
  // tidies its own swipe state on it).
  _docRelease(event) {
    if (Date.now() > this._swallowUntil) return
    event.preventDefault()
    event.stopPropagation()
    this._swallowUntil = 0
  }

  // ── State ────────────────────────────────────────────────────────

  _setState(state) {
    if (state !== "pressing") clearTimeout(this._holdTimer)
    if (state !== "open") {
      clearTimeout(this._pickTimer)
      this._picking = false
      this.itemTargets.forEach((item) => item.classList.remove("is-pick"))
    }
    this._state = state
    this.element.classList.toggle("cs-open", state === "open")
  }

  // Leaving the page or the app: back to idle, so a Turbo snapshot or a
  // return visit never shows a dimmed stage.
  _reset() {
    this._swallowUntil = 0
    this._heldOpen = false
    this._setState("idle")
  }

  // ── Layout ───────────────────────────────────────────────────────

  // The band spans the phone's width at the card's height. Layout offsets,
  // never rects or classes on the card: the card is not touched. If the card
  // is mid-handover and has no box yet, the stage's content box stands in.
  _place() {
    const card = this._activeCard()
    const pad = getComputedStyle(this.stage)
    const box = card || this.stage
    const { left, top } = this._offsetWithin(box)
    const inset = card ? left : parseFloat(pad.paddingInlineStart) || 0
    const y = card ? top : top + (parseFloat(pad.paddingTop) || 0)
    const height = card ? card.offsetHeight : this.stage.offsetHeight - (parseFloat(pad.paddingTop) || 0) - (parseFloat(pad.paddingBottom) || 0)
    Object.assign(this.viewTarget.style, { top: `${y}px`, height: `${height}px` })
    this.stripTarget.style.setProperty("--cs-gutter", `${Math.max(0, inset)}px`)
    const here = this._activeIndex()
    const cards = this._cards()
    this.itemTargets.forEach((item, index) => {
      item.classList.toggle("is-here", index === here)
      this._dress(item, cards[index])
    })
  }

  // Each ring card wears its dashboard card's skin, read at open so a skin
  // just changed on Edit budget shows: the same data-skin
  // budget_skins_controller put on the card, painted by the shared
  // .budget-skin-surface (application.css), as the neighbour peeks are.
  _dress(item, card) {
    const skin = card?.dataset.skin
    item.classList.toggle("budget-skin-surface", Boolean(skin))
    if (skin) item.dataset.skin = skin
    else delete item.dataset.skin
  }

  // Centres the card you are on in the strip. The browser clamps the scroll
  // at either end, so the first card stays at the left edge and the last at
  // the right. Measured without the entrance offset; a positive delta moves
  // the view toward the right edge in RTL too.
  _reveal(item) {
    if (!item) return
    const strip = this.stripTarget
    item.style.transition = "none"
    item.style.translate = "0 0"
    item.style.scale = "1"
    const view = strip.getBoundingClientRect()
    const rect = item.getBoundingClientRect()
    item.style.removeProperty("translate")
    item.style.removeProperty("scale")
    void item.offsetWidth
    item.style.removeProperty("transition")
    strip.scrollLeft += (rect.left + rect.width / 2) - (view.left + view.width / 2)
  }

  _offsetWithin(el) {
    let left = 0
    let top = 0
    for (let node = el; node && node !== this.element; node = node.offsetParent) {
      left += node.offsetLeft
      top += node.offsetTop
      if (!this.element.contains(node.offsetParent)) break
    }
    return { left, top }
  }

  // The carousel's own index (its value attribute), not a class: during a
  // handover two cards can carry .is-active for a moment.
  _activeIndex() {
    const index = parseInt(this.carousel?.dataset.budgetCarouselActiveIndexValue, 10)
    return Number.isNaN(index) ? 0 : index
  }

  _cards() {
    return this.element.querySelectorAll('[data-budget-carousel-target="card"]')
  }

  _activeCard() {
    const card = this._cards()[this._activeIndex()]
    return card && card.offsetHeight > 0 ? card : null
  }

  _coachDone() {
    try { return localStorage.getItem(COACH_KEY) === "1" } catch { return false }
  }
}
